import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import {
  DataGridPro,
  GridLogicOperator,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import {
  ShepPagination,
  ShepCheckbox,
  ShepTooltip,
  ShepDataGridPro,
} from "@/components/GenericComponents/ShepComponents";
import {
    ProcessStatusSuccess,
    ProcessStatusError,
    ProcessStatusErrorHover,
    ProcessStatusProcessing,
  PopUpClicked,
  PopUpInactive,
} from "../../ImageRepo";

//http://confluence.edisoft.com:8090/x/pjqWB
const RenderStatus = (status) => {
  return (
    <>
          {status == "Error" && <img alt="error" src={ProcessStatusError} style={{ width: 20, height: 20 }}></img>}
          {status == "Warning" && <img alt="warning" src={ProcessStatusError} style={{ width: 20, height: 20 }}></img>}
          {status == "Pending" && <img alt="pending" src={ProcessStatusProcessing} style={{ width: 20, height: 20 }}></img>}
          {status == "Success" && <img alt="success" src={ProcessStatusSuccess} style={{ width: 20, height: 20 }}></img>}
      <div style={{ marginLeft: 5 }}>{status}</div>
    </>
  );
};
const RenderPopUp = (id, value, callback) => {
  const onClick = () => {
    callback(id);
  };
  return (
    <div
      className="pop-up-icon"
      style={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
      }}
    >
      <div>{value}</div>
      <div onClick={onClick}>
        <img className="inactive" src={PopUpInactive}></img>
        <img className="clicked" src={PopUpClicked}></img>
      </div>
    </div>
  );
};
const CardDataGrid = forwardRef((props, ref) => {
  const RETURN_DATA_CAP = 1000; //controls how much data is returned for the front end. .Net Backend (GridDataAccess.cs) must be set to same value
  const gridRef = useGridApiRef();
  useImperativeHandle(ref, () => ({
    getGridFilter() {
      return filterModel;
    },
    setGridFilter(newFilterModel) {
      setFilterModel(newFilterModel);
    },
    selectAll(selection) {
      let data = props.rows;
      if (data.length < 1) return;
      let range = { startId: data[0].id, endId: data[data.length - 1].id };
      if (selection) {
        gridRef.current.selectRowRange(range);
      } else {
        gridRef.current.selectRowRange(range, false);
      }
    },
  }));
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 5,
    page: 0,
  });
  const [filterModel, setFilterModel] = useState({
    items: [],
    logicOperator: GridLogicOperator.And,
  });
  const [sortModel, setSortModel] = useState();
  const [selectionModel, setSelectionModel] = useState([]);

  const getRowSpacing = React.useCallback((params) => {
    return {
      top: params.isFirstVisible ? 0 : 6,
      bottom: params.isLastVisible ? 8 : 6,
    };
  }, []);

  const handleRowSelectionChange = (newModel) => {
    if (props.forceDisableMultipleSelection)
      setSelectionModel(
        (newModel = newModel.filter((newId) => !selectionModel.includes(newId)))
      );
    else setSelectionModel(newModel);
  };

  useEffect(() => {
    const map = gridRef.current.getSelectedRows();
    let array = Array.from(map, ([name, value]) => ({ ...value }));
    if (props.onSelectedRowsChange) props.onSelectedRowsChange(array);
  }, [selectionModel]);

  const onPaginationModelChange = (model) => {
    let maxPage = Math.ceil(props.rows.length / model.pageSize);
    if (
      maxPage === model.page + 1 &&
      props.rows.length % RETURN_DATA_CAP === 0
    ) {
      if (props.onHitDataCap != null) {
        props.onHitDataCap();
      }
    }
    setPaginationModel(model);
  };
  const onSortModelChange = (model) => {
    if (props.onSortChange != null) {
      props.onSortChange(model);
    }
    setSortModel(model);
  };

  useEffect(() => {
    setSelectionModel([]);
  }, [props.rows]);

  useEffect(() => {
    let newModel = { ...paginationModel };
    newModel.pageSize = props.pageSize ?? newModel.pageSize;
    onPaginationModelChange(newModel);
  }, [props.pageSize]);

  return (
    <ShepDataGridPro
      isDataLoaded={props.isDataLoaded}
      secondsToStall={11}
      experimentalFeatures={{ columnGrouping: true }}
      columnGroupingModel={props.columnGroupingModel ?? []}
      apiRef={gridRef}
      className={`CardDataGrid ${
        props.whiteHeader ?? false ? "altHeader" : "defaultHeader"
      }`}
      rows={props.rows}
      columns={props.columns.map((value) => {
        let tempData = { ...value };
        tempData.editable = false;
        tempData.hideable = false;
        return tempData;
      })}
      initialState={Object.assign(props.initialState ?? "", {
        pagination: {
          paginationModel: { pageSize: 15, page: 1 },
        },
      })}
      filterModel={filterModel}
      onFilterModelChange={setFilterModel}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      pagination={true}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      disableColumnMenu
      disableColumnReorder={true}
      disableColumnResize={false}
      getRowSpacing={getRowSpacing}
      rowHeight={props.rowHeight ?? 60}
      columnHeaderHeight={props.columnHeaderHeight ?? 60}
      checkboxSelection={props.checkboxSelection ?? false}
      disableMultipleColumnsFiltering={true}
      disableRowSelectionOnClick={props.disableRowSelectionOnClick ?? false}
      sx={{
        "& .MuiDataGrid-columnHeadersInner--scrollable, .MuiDataGrid-virtualScrollerRenderZone, .MuiDataGrid-columnHeadersInner":
          {
            marginLeft:
              (props.pinnedGapCount ??
                props.initialState?.pinnedColumns?.left.length ??
                0) * 2,
          },
        border: 0,
        "& .MuiDataGrid-columnHeaderTitleContainerContent .MuiCheckbox-root": {
          display: props.forceDisableMultipleSelection ? "none" : "flex",
        },
        "& .MuiDataGrid-selectedRowCount": {
          visibility: props.forceDisableMultipleSelection
            ? "hidden"
            : "visible",
        },
        "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover": {
          borderBottomLeftRadius: "4px",
          borderBottomRightRadius: "4px",
          backgroundColor: "var(--primary-pale)",
          borderBottom: "none",
          borderLeft: "none",
        },
        color: "var(--text-primary)",
        fontSize: "var(--font-sm)",
        fontFamily: "var(--font-family)",
        borderTopLeftRadius: "12px",
        borderTopRightRadius: "12px",
        backgroundColor: "var(--default-light)",
        border: "none",
        "& .MuiDataGrid:hover": {
          backgroundColor: "inherit",
        },
        "& .MuiDataGrid-cell": {
          borderBottom: "none",
        },
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "var(--tertiary-13)",
        },
        "& .workflow-row:hover": {
          backgroundColor: "transparent",
        },
        "& .Mui-selected": {
          backgroundColor: "var(--primary-pale)",
        },
        "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox":
          {
            outline: "none",
          },
        "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
          {
            opacity: 1,
          },
        "& .MuiDataGrid-cellCheckbox": {
          borderLeft: "8px solid transparent",
        },
        "& .Mui-selected .MuiDataGrid-cellCheckbox": {
          opacity: 1,
          borderLeft: "8px solid var(--primary-main)",
          borderTopLeftRadius: "4px",
          borderBottomLeftRadius: "4px",
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontSize: "var(--font-md)",
          fontWeight: "var(--font-bold)",
          borderLeft: "none",
        },
        "& .MuiCheckbox-root": {
          display: "block",
          paddingBottom: "20px",
        },
      }}
      slots={{
        pagination: ShepPagination,
        baseCheckbox: ShepCheckbox,
        baseTooltip: ShepTooltip,
      }}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={handleRowSelectionChange}
    />
  );
});

export default CardDataGrid;
export { GridLogicOperator, RenderStatus, RenderPopUp };
/* == Usage Notes (CY) ==
Sizing:
The grid will fill out the parent element so I recomend throwing it in a Box 
Width: 
a) including at least one column with flex = 1
b) setting a maxwidth on the parent Box that matches the total width of columns + gaps (prefered if verital gaps)
Height:
a) if you want to ensure that the grid doesn't shrink when there is less data, add a height to the parent Box
This grid component does not include the title card or filters panels
*/
